'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Box, Flex, Text } from '@chakra-ui/react';

export type CountdownBarData = {
  id: string;
  title: string;
  subtitle: string | null;
  image: string | null;
  start_date: string;
  end_date: string;
  end_message: string;
  button_label: string | null;
  button_link: string | null;
  bg_color: string;
  text_color: string;
};

const UNITS = [
  { label: 'dias', ms: 86_400_000 },
  { label: 'horas', ms: 3_600_000 },
  { label: 'min', ms: 60_000 },
  { label: 'seg', ms: 1000 },
];

function splitRemaining(ms: number): number[] {
  let rest = Math.max(0, ms);
  return UNITS.map(({ ms: size }) => {
    const value = Math.floor(rest / size);
    rest -= value * size;
    return value;
  });
}

const CountdownBarClient = ({
  bar,
  renderedAt,
}: {
  bar: CountdownBarData;
  renderedAt: number;
}) => {
  // Começa na hora em que o servidor gerou a página (o HTML tem de bater
  // certo na hidratação) e passa logo para a hora real do browser.
  const [now, setNow] = useState(renderedAt);

  useEffect(() => {
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const start = new Date(bar.start_date).getTime();
  const end = new Date(bar.end_date).getTime();

  if (now < start) return null;

  const ended = now >= end;
  const parts = splitRemaining(end - now);
  const isExternal = !!bar.button_link?.startsWith('https://');

  const button = bar.button_label && bar.button_link && (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      h="36px"
      px="18px"
      borderRadius="full"
      fontSize="13px"
      fontWeight="700"
      whiteSpace="nowrap"
      transition="opacity 0.2s"
      _hover={{ opacity: 0.85 }}
      style={{ background: bar.text_color, color: bar.bg_color }}
    >
      {bar.button_label}
    </Box>
  );

  return (
    <Flex
      w="full"
      justify="center"
      px={{ base: '16px', md: '24px' }}
      py={{ base: '10px', md: '12px' }}
      style={{ background: bar.bg_color, color: bar.text_color }}
      role="region"
      aria-label={bar.title}
    >
      <Flex
        w="full"
        maxW="breakpoint-xl"
        align="center"
        justify="space-between"
        gap={{ base: '10px', md: '20px' }}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
      >
        <Flex align="center" gap="12px" minW="0" flex="1">
          {bar.image && (
            <Box
              pos="relative"
              w={{ base: '36px', md: '44px' }}
              h={{ base: '36px', md: '44px' }}
              flexShrink={0}
            >
              <Image
                src={bar.image}
                alt=""
                fill
                sizes="44px"
                style={{ objectFit: 'contain' }}
              />
            </Box>
          )}
          <Flex flexDir="column" minW="0">
            <Text
              fontSize={{ base: '14px', md: '15px' }}
              fontWeight="800"
              lineHeight="1.25"
            >
              {bar.title}
            </Text>
            {bar.subtitle && (
              <Text fontSize="12px" opacity={0.85} lineHeight="1.3">
                {bar.subtitle}
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex
          align="center"
          gap={{ base: '10px', md: '16px' }}
          justify={{ base: 'space-between', md: 'flex-end' }}
          w={{ base: 'full', md: 'auto' }}
        >
          {ended ? (
            <Text fontSize={{ base: '13px', md: '14px' }} fontWeight="700">
              {bar.end_message}
            </Text>
          ) : (
            <Flex gap="6px" aria-live="off">
              {parts.map((value, i) => (
                <Flex
                  key={UNITS[i].label}
                  flexDir="column"
                  align="center"
                  minW={{ base: '44px', md: '50px' }}
                  py="4px"
                  borderRadius="8px"
                  style={{
                    background:
                      'color-mix(in srgb, currentColor 14%, transparent)',
                  }}
                >
                  <Text
                    fontSize={{ base: '16px', md: '18px' }}
                    fontWeight="800"
                    lineHeight="1"
                    fontVariantNumeric="tabular-nums"
                  >
                    {String(value).padStart(2, '0')}
                  </Text>
                  <Text fontSize="10px" opacity={0.8} lineHeight="1.2">
                    {UNITS[i].label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}

          {button &&
            (isExternal ? (
              <a
                href={bar.button_link as string}
                target="_blank"
                rel="noopener noreferrer"
              >
                {button}
              </a>
            ) : (
              <Link href={bar.button_link as string}>{button}</Link>
            ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CountdownBarClient;
